import React from "react";
import { convertDateTime } from "Helpers/date";
import { formatUsPhone } from "Hooks/commanUtility";
import Logo from "Assets/images/LockUp-Horizontal_Black.png";
import styles from "./index.module.scss";
import {
  formatAddressLines,
  formatCurrency,
  formatName,
  getReceiptItems,
  getReceiptTotals,
  humanizeStatus,
} from "./receiptUtils";

const Detail = ({ label, value }) => (
  <div className={styles.detail}>
    <span>{label}</span>
    <strong>{value || "Not provided"}</strong>
  </div>
);

const Address = ({ title, name, lines, email, phone }) => (
  <section className={styles.addressCard}>
    <h2>{title}</h2>
    <strong className={styles.addressName}>{name}</strong>
    {lines.map((line, index) => (
      <div key={`${line}-${index}`}>{line}</div>
    ))}
    {email && <div className={styles.contactLine}>{email}</div>}
    {phone && <div>{phone}</div>}
  </section>
);

const formatPhone = (countryCode, phone) =>
  phone
    ? [countryCode, formatUsPhone(phone)].filter(Boolean).join(" ")
    : "";

const formatOrderDate = (value, timezone) =>
  value
    ? convertDateTime(value, "iso", "MMM dd, yyyy • h:mm a", "", timezone)
    : "Not provided";

const formatDeliveryDate = (value, timezone) =>
  value
    ? convertDateTime(value, "iso", "MMM dd, yyyy", "", timezone)
    : "Not provided";

export const OrderReceiptDocument = ({ order }) => {
  const transaction = order?.order_transaction || {};
  const recipients = order?.order_recipients || [];
  const timezone = order?.store?.timezone;
  const totals = getReceiptTotals(order);
  const isPickup = Boolean(order?.order_type);

  const billingAddress = formatAddressLines({
    addressLine1: transaction.customer_billing_address_1,
    city: transaction.customer_billing_city,
    state: transaction.customer_billing_state,
    zipcode: transaction.customer_billing_zipcode,
    country: transaction.customer_billing_country,
  });

  return (
    <article className={styles.receiptPaper} data-testid="order-receipt">
      <header className={styles.receiptHeader}>
        <img className={styles.logo} src={Logo} alt="French Florist" />
        <div className={styles.receiptTitle}>
          <span>Order #{order?.order_number || "—"}</span>
          <h1>Itemized Receipt</h1>
        </div>
      </header>

      <section className={styles.orderDetails}>
        <Detail label="Order #" value={order?.order_number} />
        <Detail
          label="Order date"
          value={formatOrderDate(order?.createdAt, timezone)}
        />
        <Detail
          label={isPickup ? "Pickup date" : "Delivery date"}
          value={formatDeliveryDate(order?.delivery_date_time, timezone)}
        />
        <Detail label="Store" value={order?.store?.title} />
        <Detail label="Status" value={humanizeStatus(order?.order_status)} />
        {order?.burq_order_id && (
          <Detail label="Burq order ID" value={order.burq_order_id} />
        )}
      </section>

      <div className={styles.addressGrid}>
        <Address
          title="Customer"
          name={formatName(
            transaction.customer_first_name,
            transaction.customer_last_name
          )}
          lines={[]}
          email={transaction.customer_email}
          phone={formatPhone(
            transaction.customer_phone_country_code,
            transaction.customer_phone
          )}
        />
        <Address
          title="Billing address"
          name={formatName(
            transaction.customer_billing_first_name,
            transaction.customer_billing_last_name
          )}
          lines={billingAddress}
        />
      </div>

      <div className={styles.recipientList}>
        {recipients.map((recipient, recipientIndex) => {
          const items = getReceiptItems(recipient);
          const shippingAddress = formatAddressLines({
            attention: recipient.attn,
            addressLine1: recipient.address_line_1,
            addressLine2: recipient.address_line_2,
            city: recipient.city,
            state: recipient.state,
            zipcode: recipient.zipcode,
            country: recipient.country,
          });

          return (
            <section
              className={styles.recipientSection}
              key={recipient.id || recipientIndex}
            >
              <div className={styles.recipientHeader}>
                <div>
                  <span className={styles.eyebrow}>
                    {isPickup ? "Pickup recipient" : "Shipping address"}
                    {recipients.length > 1 ? ` ${recipientIndex + 1}` : ""}
                  </span>
                  <h2>
                    {formatName(recipient.first_name, recipient.last_name)}
                  </h2>
                </div>
                <div className={styles.shippingAddress}>
                  {shippingAddress.map((line, index) => (
                    <div key={`${line}-${index}`}>{line}</div>
                  ))}
                  {recipient.phone && (
                    <div>
                      {formatPhone(
                        recipient.phone_country_code,
                        recipient.phone
                      )}
                    </div>
                  )}
                </div>
              </div>

              <table className={styles.itemTable}>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Item</th>
                    <th>Variation</th>
                    <th className={styles.numberColumn}>Qty</th>
                    <th className={styles.moneyColumn}>Unit price</th>
                    <th className={styles.moneyColumn}>Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length ? (
                    items.map((item, itemIndex) => (
                      <tr key={item.id || itemIndex}>
                        <td>
                          <span className={styles.itemType}>
                            {item.itemType}
                          </span>
                        </td>
                        <td>{item.product_name || "Unnamed item"}</td>
                        <td>{item.variation_name || "—"}</td>
                        <td className={styles.numberColumn}>
                          {item.qty ?? 0}
                        </td>
                        <td className={styles.moneyColumn}>
                          {formatCurrency(item.unit_price)}
                        </td>
                        <td className={styles.moneyColumn}>
                          {formatCurrency(item.total_price)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className={styles.emptyItems}>
                        No receipt items found for this recipient.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          );
        })}
      </div>

      <section className={styles.totalsSection}>
        <div className={styles.totals}>
          {totals.map((total) => (
            <div className={styles.totalRow} key={total.label}>
              <span>{total.label}</span>
              <span>{formatCurrency(total.value)}</span>
            </div>
          ))}
          <div className={styles.grandTotal}>
            <span>Total</span>
            <span>{formatCurrency(order?.total)}</span>
          </div>
        </div>
      </section>
    </article>
  );
};

export default OrderReceiptDocument;
